<template>
  <div>
    <Title :pages="[{ icon: 'file-invoice', page: page_title.toUpperCase() }]"></Title>

    <v-container fluid>
      <v-alert v-if="pending > 0" type="warning" text dense class="mb-5">
        Tenés <b>{{ pending }}</b> {{ pending == 1 ? "recibo pendiente" : "recibos pendientes" }} de firma. Abrí cada recibo, leelo y firmalo.
      </v-alert>

      <v-card class="mb-5 px-4 py-6">
        <div class="row">
          <div class="col-md-3">
            <v-select v-model="period_year" :items="years" label="Año" clearable hide-details outlined dense></v-select>
          </div>
          <div class="col-md-3">
            <v-select v-model="type" :items="types" item-text="name" item-value="id" label="Tipo" clearable hide-details
              outlined dense></v-select>
          </div>
          <div class="col-md-6" style="display: flex; justify-content: end; align-items: center">
            <v-btn color="primary" @click="loadList">Buscar</v-btn>
          </div>
        </div>
      </v-card>

      <div class="lc-loose">
        <v-data-iterator :items="loading ? [] : list" :loading="loading" :hide-default-footer="loading" :items-per-page="15"
          :footer-props="{ itemsPerPageOptions: [5, 10, 15, 50, -1] }">
          <template v-slot:loading>
            <ListSkeletonRows :count="4" />
          </template>
          <template v-slot:default="props">
            <div class="lc-list">
              <div v-for="payslip in props.items" :key="payslip.id" class="lc-item mp-row">
                <div class="lc-main mp-main">
                  <div class="lc-info">
                    <div class="lc-title">{{ periodTitle(payslip) }}</div>
                    <div class="lc-sub">{{ payslip.type ? payslip.type.name : "" }} · Pago {{ formatDate(payslip.payment_date) }}</div>
                  </div>
                </div>
                <div class="lc-meta">
                  <div class="lc-meta-col mp-col-date">
                    <template v-if="payslip.signed_at">Firmado {{ formatDate(payslip.signed_at) }}</template>
                    <template v-else>Publicado {{ formatDate(payslip.published_at) }}</template>
                  </div>
                  <div class="mp-col-status">
                    <v-chip small :color="statusChip(payslip).color" :text-color="statusChip(payslip).text_color">
                      {{ statusChip(payslip).name }}
                    </v-chip>
                  </div>
                  <div class="lc-actions mp-col-actions">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn icon small v-on="on" :loading="opening == payslip.id" @click.stop="openFile(payslip)">
                          <v-icon small color="blue">mdi-file-pdf-box</v-icon>
                        </v-btn>
                      </template>
                      <span>Ver recibo</span>
                    </v-tooltip>
                    <v-tooltip v-if="payslip.status != signed" bottom :disabled="payslip.status != published">
                      <template v-slot:activator="{ on }">
                        <div v-on="on" class="ml-2">
                          <v-btn small depressed color="primary" class="mp-sign-btn" :disabled="payslip.status == published" @click.stop="openSign(payslip)">
                            <v-icon small left>mdi-draw-pen</v-icon> Firmar
                          </v-btn>
                        </div>
                      </template>
                      <span>Abrí el recibo para poder firmarlo</span>
                    </v-tooltip>
                    <v-btn v-else small outlined color="green" class="ml-2 mp-sign-btn" @click.stop="openSign(payslip)">
                      <v-icon small left>mdi-check-decagram</v-icon> Ver firma
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-slot:no-data>
            <div class="lc-empty">No tenés recibos para los filtros seleccionados.</div>
          </template>
        </v-data-iterator>
      </div>
    </v-container>

    <ModalSignPayslip :dialog="dialog_sign" :payslip="selected_payslip" :signature-loader="signatureLoader"
      @updateDialog="(value) => (dialog_sign = value)"
      @signed="loadList" />
  </div>
</template>

<script>
import Title from "@/components/Title.vue";
import { myPayslipService } from "@/libs/ws/myPayslipService";
import { openPdfInNewTab } from "@/libs/utils/pdfTab";
import ListSkeletonRows from "@/components/ListSkeletonRows.vue";
import ModalSignPayslip from "./ModalSignPayslip.vue";
import {
  PAYSLIP_TYPES,
  PAYSLIP_STATUS_SIGNED,
  PAYSLIP_STATUS_PUBLISHED,
  EMPLOYEE_STATUSES,
  signedChip,
  periodTitle,
  yearOptions,
  formatDate,
} from "@/constants/payslips";

export default {
  components: { Title, ListSkeletonRows, ModalSignPayslip },
  data() {
    return {
      page_title: "Mis recibos",
      list: [],
      pending: 0,
      loading: false,
      period_year: null,
      type: null,
      years: yearOptions(),
      types: PAYSLIP_TYPES,
      signed: PAYSLIP_STATUS_SIGNED,
      published: PAYSLIP_STATUS_PUBLISHED,
      opening: null,
      selected_payslip: null,
      dialog_sign: false,
      signatureLoader: myPayslipService.signature,
      periodTitle: periodTitle,
      formatDate: formatDate,
    };
  },
  mounted() {
    this.loadList();
  },
  methods: {
    loadList() {
      this.loading = true;
      myPayslipService
        .filter({ period_year: this.period_year, type: this.type })
        .then((response) => {
          if (response.status) {
            this.list = response.result.list;
            this.pending = response.result.pending;
            this.$store.commit("setMyPayslipsPending", this.pending);
          } else {
            this.$toast.error(response.msg, "Error");
          }
        })
        .catch((e) => console.log(e))
        .finally(() => (this.loading = false));
    },
    statusChip(payslip) {
      if (payslip.status == PAYSLIP_STATUS_SIGNED) return signedChip(payslip);
      return EMPLOYEE_STATUSES[payslip.status] || { name: payslip.status };
    },
    // Abre el PDF en otra pestaña (el firmado si ya lo firmo). La primera apertura registra la lectura y habilita la firma.
    openFile(payslip) {
      if (this.opening) return;
      this.opening = payslip.id;
      openPdfInNewTab(myPayslipService.file(payslip.id))
        .then((response) => {
          if (!response.status) this.$toast.error(response.msg, "Error");
          else if (payslip.status == PAYSLIP_STATUS_PUBLISHED) this.loadList();
        })
        .catch((e) => {
          console.log(e);
          this.$toast.error("No se pudo abrir el recibo.", "Error");
        })
        .finally(() => (this.opening = null));
    },
    openSign(payslip) {
      this.selected_payslip = payslip;
      this.dialog_sign = true;
    },
  },
};
</script>

<style scoped>
.mp-row {
  flex-wrap: wrap;
}
/* Si el período no llega a este ancho, los datos pasan a un segundo renglón */
.mp-main {
  flex: 1 1 240px;
}
.mp-col-date {
  width: 130px;
}
.mp-col-status {
  width: 190px;
  display: flex;
  justify-content: center;
}
.mp-col-actions {
  width: 165px;
  justify-content: flex-end;
}
/* Mismo ancho en "Firmar" y "Ver firma" para que el ícono del PDF quede alineado entre filas */
.mp-sign-btn {
  min-width: 125px !important;
}
@media (max-width: 768px) {
  .mp-main {
    flex: none;
  }
  .mp-col-date,
  .mp-col-status,
  .mp-col-actions {
    width: auto;
  }
  /* Los botones de texto van debajo, no en la columna flotante de las filas */
  .mp-col-actions {
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin-top: 8px;
  }
}
</style>
